import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Send, Bot, User, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ChatMessage {
  role: string;
  content: string;
  session_id: string;
}

interface ChatBotProps {
  showFullContent?: boolean;
}

const ChatBot = ({ showFullContent = true }: ChatBotProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const { toast } = useToast();

  // Initialize session ID
  useEffect(() => {
    const existingSessionId = sessionStorage.getItem('chatSessionId');
    const newSessionId = existingSessionId || crypto.randomUUID();
    
    if (!existingSessionId) {
      sessionStorage.setItem('chatSessionId', newSessionId);
    }
    
    setSessionId(newSessionId);
    console.log("Chat session ID:", newSessionId);
  }, []);

  // Load existing messages for this session only
  useEffect(() => {
    const loadMessages = async () => {
      if (!sessionId) return;

      try {
        const { data, error } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true });

        if (error) {
          console.error('Error loading messages:', error);
          return;
        }

        if (data && data.length > 0) {
          const typedMessages: Message[] = data.map(msg => ({
            role: msg.role as "user" | "assistant",
            content: msg.content
          }));
          setMessages(typedMessages);
          console.log(`Loaded ${data.length} messages for session ${sessionId}`);
        }
      } catch (err) {
        console.error('Exception loading messages:', err);
      }
    };

    if (sessionId) {
      loadMessages();
    }
  }, [sessionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !sessionId) return;

    const newMessage: Message = { role: "user", content: input };
    setMessages((prev) => [...prev, newMessage]);
    setInput("");
    setIsLoading(true);

    try {
      // Store user message in the database
      const userMessage: ChatMessage = {
        content: input,
        role: 'user',
        session_id: sessionId
      };

      const { error: userMsgError } = await supabase
        .from('chat_messages')
        .insert([userMessage]);

      if (userMsgError) {
        console.error('Error saving user message:', userMsgError);
      }

      // Get bot response
      const { data, error } = await supabase.functions.invoke('chat', {
        body: { message: input }
      });

      if (error) throw error;

      const botResponse = data.response || data.message;
      
      // Store bot response in the database
      const assistantMessage: ChatMessage = {
        content: botResponse,
        role: 'assistant',
        session_id: sessionId
      };

      const { error: botMsgError } = await supabase
        .from('chat_messages')
        .insert([assistantMessage]);

      if (botMsgError) {
        console.error('Error saving bot response:', botMsgError);
      }

      // Update UI with bot response
      const botResponseMsg: Message = {
        role: "assistant",
        content: botResponse,
      };
      setMessages((prev) => [...prev, botResponseMsg]);
    } catch (error) {
      console.error('Error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to get response from the chatbot. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <Card className="shadow-xl border-2 border-primary/10">
        <CardHeader className="border-b bg-primary/5">
          <CardTitle className="flex items-center gap-2 text-primary">
            <Bot className="h-6 w-6" />
            SpudBot Assistant
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="h-[500px] overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 ${
                  message.role === "user" ? "flex-row-reverse" : "flex-row"
                } animate-fade-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div
                  className={`flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-full ${
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent text-accent-foreground"
                  }`}
                >
                  {message.role === "user" ? (
                    <User className="h-5 w-5" />
                  ) : (
                    <Bot className="h-5 w-5" />
                  )}
                </div>
                <div
                  className={`flex max-w-[80%] rounded-lg px-4 py-2 text-sm ${
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-[#166534] text-white"
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground space-y-4">
                <Bot className="h-16 w-16 text-primary animate-pulse" />
                <div className="space-y-2">
                  <p className="text-xl font-medium text-primary">Welcome to SpudBot!</p>
                  <p className="text-sm max-w-sm mx-auto">
                    Your intelligent assistant for all things seed potatoes. Ask me about cultivation, 
                    disease management, storage practices, and more!
                  </p>
                </div>
              </div>
            )}
            {isLoading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">SpudBot is thinking...</span>
              </div>
            )}
          </div>
          <div className="border-t p-4 bg-muted/50">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about seed potatoes..."
                className="flex-1"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={isLoading}
                className="bg-primary hover:bg-primary/90"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {showFullContent && (
        <>
          <div className="text-center space-y-4">
            <p className="text-muted-foreground text-lg">
              Our chatbot is here to help you with all things related to seed potatoes and our organisation. 
              Whether you're looking for specific information on potato management, or have a general question, 
              SpudBot is ready to assist. It's powered by Artificial Intelligence and retrieves the most 
              relevant information from our knowledge library.
            </p>
            <p className="text-muted-foreground text-lg">
              Just type in your question, and let the SpudBot do the rest!
            </p>
          </div>

          <Alert className="text-left">
            <AlertDescription className="text-sm">
              <strong>Important Disclaimer:</strong> Spudbot strives to provide helpful information, 
              however we cannot guarantee the information will be complete, accurate, or up to date. 
              It is a good idea to seek advice from professionals such as your agronomist.
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
};

export default ChatBot;
